import {
  cloneDeep,
  defaults,
  entries,
  fromPairs,
  groupBy,
  isEmpty,
  isFunction,
  isNull,
  keyBy,
  sortBy,
  sumBy,
} from "lodash";
import AdminService from "../../../../services/admin";
import moment from "moment/moment";
import Config from "../../../../config";
import { FilterModel } from "../model";
import { transformFloatToHumanReadable } from "../../../../utils";

const { PRICING_TIERS_ORDER } = Config;

export const DAILY_STAT_TABLE_DATA_START_DATE = "2026-03-01";

const SUMMATION_KEY_MAP = Object.freeze({
  numNewConversions: "new.card",
  numNewConversionsCash: "new.cash",
  numRenewConversions: "renew.card",
  numRenewConversionsCash: "renew.cash",
});

export const WIDGET_TYPE = Object.freeze({
  default: { id: "default", label: "default", default: true },
  operatorPricingTierConvertions: {
    id: "operatorPricingTierConvertions",
    label: "Operator/Package Wise Convertions",
  },
});

const initializePage = async ({ setOperators }) => {
  try {
    if (isFunction(setOperators)) {
      const response = await AdminService.getAllOperators();
      if (!response.success) {
        // TODO: Error Handling
      } else {
        const operators = Array.isArray(response.body) ? response.body : [];
        setOperators(operators);
      }
    }
  } catch (error) {
    console.log(">>> error ", error);
  }
};

const onSubmitFilterForm = async ({
  values,
  actions,
  operators,
  pricingPackages = [],
  setRawApiData,
}) => {
  const output = {};
  try {
    const from = moment(values.fromDate);
    const to = moment(values.toDate);

    // Historical operator stats (before March 1, 2026) may contain inconsistencies.
    // Daily stats recorded from March 1, 2026 onward are accurate.
    const shouldShowOldOperatorStats =
      from.isBefore(moment(DAILY_STAT_TABLE_DATA_START_DATE).startOf("day")) ||
      to.isBefore(moment(DAILY_STAT_TABLE_DATA_START_DATE).startOf("day"));

    const pricingTierIds = pricingPackages.map((item) => item.id);

    const initialConversionTotals = Object.keys(SUMMATION_KEY_MAP).reduce(
      (accumulator, key) => {
        accumulator[key] = 0;
        return accumulator;
      },
      {},
    );

    for (const operator of operators) {
      const operatorId = operator.memberId;
      actions.setSubmitting(true);

      let response = {};

      if (shouldShowOldOperatorStats) {
        response = await AdminService.getOperatorStat({
          operatorId,
          from: from.format("YYYY-MM-DD"),
          to: to.format("YYYY-MM-DD"),
        });
      } else {
        response = await AdminService.getOperatorDailyStats({
          operatorId,
          from: from.format("YYYY-MM-DD"),
          to: to.format("YYYY-MM-DD"),
        });
      }

      if (!response.success) {
        // TODO: Error Handling
        continue;
      }

      const mapById = Object.fromEntries(
        (response.body.conversions ?? []).map((c) => [c.pricingTierId, c]),
      );
      const conversions = pricingTierIds.map(
        (id) =>
          mapById[id] || { pricingTierId: id, ...initialConversionTotals },
      );

      const tempResponse = response.body;
      tempResponse.conversions = conversions;

      output[operatorId] = tempResponse;
    }
    if (isFunction(setRawApiData)) {
      setRawApiData(output);
    }
  } catch (error) {
    console.log(">>>> error ", error);
  } finally {
    actions.setSubmitting(false);
  }
};

const generateTableDataset = ({ rawApiData, setTableDataset }) => {
  const output = {};
  for (const [operatorId, apiData] of Object.entries(rawApiData)) {
    let tempResult = accumulateOperatorConversions(apiData);
    tempResult = mapApiOperatorStatToTableMapping(tempResult);
    output[operatorId] = {
      operator: apiData.operator,
      dataset: tempResult,
    };
  }
  if (isFunction(setTableDataset)) setTableDataset(output);
};

const generatePackageBreakdown = ({ rawApiData, setPackageBreakdown }) => {
  let output = {};
  let tempArray = Object.values(rawApiData).map(
    (element) => element.conversions,
  );
  tempArray = tempArray.flat();
  const groupedConversions = groupBy(
    tempArray,
    (element) => element.pricingTierId,
  );
  for (const [packageId, conversions] of Object.entries(groupedConversions)) {
    for (const term of Object.keys(SUMMATION_KEY_MAP)) {
      output[packageId] = output[packageId] ?? {};
      output[packageId][term] = sumBy(conversions, term);
    }
  }
  output = transformTotalPackageBreakdown(output);
  setPackageBreakdown(output);
};

const transformTotalPackageBreakdown = (input) => {
  const output = {
    packagesList: {},
    total: {},
  };
  const tempPackagesList = {};
  for (const [packageId, conversions] of Object.entries(input)) {
    const temp = {};
    for (const [term, keyPath] of Object.entries(SUMMATION_KEY_MAP)) {
      const [packageCriteria, paymentMethod] = keyPath.split(".");
      temp[packageCriteria] = temp[packageCriteria] ?? {};
      temp[packageCriteria][paymentMethod] = conversions[term];
    }
    const flattenTemp = Object.values(temp);
    const total = {};
    if (!isEmpty(flattenTemp)) {
      for (const term of Object.keys(flattenTemp[0])) {
        total[term] = sumBy(flattenTemp, term);
      }
    }
    temp.total = total;
    // output.packagesList[packageId] = temp;
    tempPackagesList[packageId] = temp;

    for (const [key, value] of Object.entries(total)) {
      output.total[key] = (output.total[key] ?? 0) + value;
    }
  }

  const sortedListOfPackagesArray = sortBy(entries(tempPackagesList), ([key]) =>
    PRICING_TIERS_ORDER.indexOf(key),
  );
  const filteredPackagesList = sortedListOfPackagesArray.filter(
    ([key, value]) => {
      return !(value.total.card === 0 && value.total.cash === 0);
    },
  );

  output.packagesList = fromPairs(filteredPackagesList);

  return output;
};

export const accumulateOperatorConversions = (input) => {
  const { numCommentsExpired, numCommentsNew, conversions } = input;
  const summationKeys = Object.keys(SUMMATION_KEY_MAP);
  let output = { numCommentsExpired, numCommentsNew };
  summationKeys.forEach((key) => {
    output[key] = 0;
  });
  output = cloneDeep(conversions).reduce((accumulator, currentValue) => {
    summationKeys.forEach((key) => {
      accumulator[key] += currentValue[key] ?? 0;
    });
    return accumulator;
  }, output);
  return output;
};

const mapApiOperatorStatToTableMapping = (operatorStat) => {
  const output = {
    new: {
      comments: operatorStat.numCommentsNew ?? 0,
      card: operatorStat.numNewConversions ?? 0,
      cash: operatorStat.numNewConversionsCash ?? 0,
    },
    renew: {
      comments: operatorStat.numCommentsExpired ?? 0,
      card: operatorStat.numRenewConversions ?? 0,
      cash: operatorStat.numRenewConversionsCash ?? 0,
    },
    total: { comments: 0, card: 0, cash: 0, new: 0, renew: 0 },
  };
  output.total.comments = output.new.comments + output.renew.comments;
  output.total.card = output.new.card + output.renew.card;
  output.total.cash = output.new.cash + output.renew.cash;
  output.total.new = output.new.card + output.new.cash;
  output.total.renew = output.renew.card + output.renew.cash;

  return output;
};

const validateFilterForm = (values) => {
  const errors = {};
  if (isNull(values.fromDate)) {
    errors.fromDate = "fromDate is required";
  }
  if (isNull(values.toDate)) {
    errors.toDate = "toDate is required";
  }
  return errors;
};

const calculateTotalByTableData = (tableData) => {
  const output = {
    new: { comments: 0, card: 0, cash: 0 },
    renew: { comments: 0, card: 0, cash: 0 },
    total: { comments: 0, card: 0, cash: 0 },
  };
  for (const current of Object.values(tableData)) {
    const operatorDataset = current.dataset ?? {};
    output.new.comments += operatorDataset.new.comments;
    output.new.card += operatorDataset.new.card;
    output.new.cash += operatorDataset.new.cash;
    output.renew.comments += operatorDataset.renew.comments;
    output.renew.card += operatorDataset.renew.card;
    output.renew.cash += operatorDataset.renew.cash;
  }
  output.total.comments = output.new.comments + output.renew.comments;
  output.total.card = output.new.card + output.renew.card;
  output.total.cash = output.new.cash + output.renew.cash;

  return output;
};

const accumulateOperatorConversionsV2 = ({ input, pricingTiers }) => {
  const { operator, conversions, numCommentsExpired, numCommentsNew } = input;
  const filteredConversions = conversions.filter((item) =>
    pricingTiers.includes(item.pricingTierId),
  );
  const output = {
    operator,
    comments: {
      new: numCommentsNew,
      renew: numCommentsExpired,
      total: numCommentsExpired + numCommentsNew,
    },
    sum: { card: 0, cash: 0, total: 0 },
    conversions: filteredConversions.map((temp) => {
      const tempObj = {
        pricingTierId: temp.pricingTierId,
        new: {
          cash: temp.numNewConversionsCash ?? 0,
          card: temp.numNewConversions ?? 0,
          total:
            (temp.numNewConversionsCash ?? 0) + (temp.numNewConversions ?? 0),
        },
        renew: {
          cash: temp.numRenewConversionsCash ?? 0,
          card: temp.numRenewConversions ?? 0,
          total:
            (temp.numRenewConversionsCash ?? 0) +
            (temp.numRenewConversions ?? 0),
        },
        summery: { card: 0, cash: 0, total: 0 },
        conversionRate: 0.0,
      };
      // package level summery calculation
      tempObj.summery.card = tempObj.new.card + tempObj.renew.card;
      tempObj.summery.cash = tempObj.new.cash + tempObj.renew.cash;
      tempObj.summery.total = tempObj.summery.cash + tempObj.summery.card;
      return tempObj;
    }),
  };
  const conversionKeyMap = keyBy(output.conversions, "pricingTierId");
  const orderedConversions = [];
  for (const pricingTierId of PRICING_TIERS_ORDER) {
    const temp = conversionKeyMap[pricingTierId];
    if (!isEmpty(temp)) {
      orderedConversions.push(temp);
    }
  }
  output.conversions = orderedConversions;
  const allConversions = orderedConversions
    .map((item) => [item.new, item.renew])
    .flat();
  output.sum.cash = sumBy(allConversions, (item) => item.cash);
  output.sum.card = sumBy(allConversions, (item) => item.card);
  output.sum.total = output.sum.cash + output.sum.card;
  // Package level ratio calculations
  const totalConversions = output.sum.total;
  output.conversions.forEach((item) => {
    const packageTotal = item.summery.total;
    if (totalConversions > 0) {
      item.conversionRate = transformFloatToHumanReadable(
        (packageTotal / totalConversions) * 100,
      );
    }
  });

  return output;
};

const generatePackageWiseConversions = ({ rawApiData, pricingTiers = [] }) => {
  const output = [];
  for (const operatorRawData of Object.values(rawApiData)) {
    const entity = accumulateOperatorConversionsV2({
      input: operatorRawData,
      pricingTiers,
    });
    output.push(entity);
  }
  return output;
};

const generateOperatorSummeryTable = ({ rawApiData }) => {
  const pricingTiers = FilterModel.pricingTiers.options.map(
    (item) => item.value,
  );
  const output = [];
  for (const operatorRawData of Object.values(rawApiData)) {
    const entity = accumulateOperatorConversionsV2({
      input: operatorRawData,
      pricingTiers,
    });
    output.push(entity);
  }
  return output;
};

const PageDomain = {
  initializePage,
  onSubmitFilterForm,
  validateFilterForm,
  calculateTotalByTableData,
  generateTableDataset,
  generatePackageBreakdown,
  generatePackageWiseConversions,
  generateOperatorSummeryTable,
};

export default PageDomain;
